import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ChevronDown, CircleX, Loader2, Search } from "lucide-react";
import LightMarkdown from "./LightMarkdown";
import ActionCard from "./ActionCard";
import type { ChatItem } from "./types";

const StepGroup: React.FC<{ labels: string[]; done: boolean }> = ({ labels, done }) => {
  const [expanded, setExpanded] = useState(false);
  if (!done) {
    return (
      <div className="flex flex-wrap gap-1.5">
        {labels.map((l, i) => {
          const isLast = i === labels.length - 1;
          return (
            <motion.span
              key={i}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs text-slate-600"
            >
              {isLast ? (
                <Loader2 className="h-3 w-3 animate-spin text-[#198754]" />
              ) : (
                <Check className="h-3 w-3 text-[#198754]" />
              )}
              {l}
            </motion.span>
          );
        })}
      </div>
    );
  }
  const n = labels.length;
  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="inline-flex min-h-[32px] items-center gap-1.5 rounded-full px-1 text-xs font-medium text-slate-500 hover:text-slate-700"
      >
        <Search className="h-3 w-3" />
        Looked up {n} {n === 1 ? "thing" : "things"}
        <ChevronDown className={`h-3 w-3 transition-transform ${expanded ? "rotate-180" : ""}`} />
      </button>
      {expanded && (
        <ul className="mt-1 space-y-1 border-l-2 border-slate-100 pl-3">
          {labels.map((l, i) => (
            <li key={i} className="flex items-center gap-1.5 text-xs text-slate-500">
              <Check className="h-3 w-3 text-[#198754]" />
              {l}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const TypingIndicator: React.FC = () => (
  <div className="flex items-center gap-1 px-1 py-2" aria-label="Assistant is working">
    {[0, 1, 2].map((i) => (
      <motion.span
        key={i}
        className="h-1.5 w-1.5 rounded-full bg-slate-400"
        animate={{ opacity: [0.3, 1, 0.3], y: [0, -2, 0] }}
        transition={{ duration: 1, repeat: Infinity, delay: i * 0.15 }}
      />
    ))}
  </div>
);

interface Props {
  items: ChatItem[];
  streaming: boolean;
  onApprove: (actionId: string, edits?: Record<string, string>) => void;
  onSkip: (actionId: string) => void;
}

const MessageList: React.FC<Props> = ({ items, streaming, onApprove, onSkip }) => {
  const endRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [items, streaming]);

  const last = items[items.length - 1];
  const showTyping = streaming && !(last && last.kind === "steps" && !last.done);

  return (
    <div className="space-y-3 px-4 py-4" role="log" aria-live="polite" aria-relevant="additions">
      <AnimatePresence initial={false}>
        {items.map((it) => (
          <motion.div
            key={it.id}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.18 }}
          >
            {it.kind === "user" && (
              <div className="flex justify-end">
                <div className="max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-br-md bg-[#0F5132] px-3.5 py-2 text-sm text-white">
                  {it.text}
                </div>
              </div>
            )}
            {it.kind === "assistant" && (
              <div className="max-w-[95%]">
                <LightMarkdown text={it.text} />
              </div>
            )}
            {it.kind === "steps" && <StepGroup labels={it.labels} done={it.done} />}
            {it.kind === "action" && (
              <ActionCard
                action={it.action}
                busy={it.busy}
                disabled={streaming}
                onApprove={(edits) => onApprove(it.action._id, edits)}
                onSkip={() => onSkip(it.action._id)}
              />
            )}
            {it.kind === "error" && (
              <div role="alert" className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                <CircleX className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{it.text}</span>
              </div>
            )}
            {it.kind === "notice" && <p className="text-center text-xs text-slate-400">{it.text}</p>}
          </motion.div>
        ))}
      </AnimatePresence>
      {showTyping && <TypingIndicator />}
      <div ref={endRef} />
    </div>
  );
};

export default MessageList;
